// src/components/ui/ProgressBar.tsx
import React from 'react';

interface ProgressBarProps {
  value: number;
  max?: number;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  color?: 'teal' | 'primary' | 'success' | 'success-dark' | 'warning' | 'danger';
  animated?: boolean;
  label?: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  size = 'md',
  showLabel = true,
  color,
  animated = true,
  label = 'Progress',
}) => {
  const percentage = Math.min(Math.round((value / max) * 100), 100);

  const sizeClasses = { sm: 'h-1.5', md: 'h-2.5', lg: 'h-4' };

  const getColorClass = () => {
    if (color) {
      const colorMap: Record<string, string> = {
        teal: 'bg-teal-500',
        primary: 'bg-blue-500',
        success: 'bg-green-500',
        'success-dark': 'bg-emerald-600',
        warning: 'bg-yellow-500',
        danger: 'bg-red-500',
      };
      return colorMap[color] || 'bg-teal-500';
    }

    if (percentage >= 100) return 'bg-green-500';
    if (percentage >= 75) return 'bg-teal-500';
    if (percentage >= 50) return 'bg-yellow-500';
    if (percentage >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const colorValueMap: Record<string, string> = {
    teal: '#14B8A6',
    primary: '#3B82F6',
    success: '#22C55E',
    'success-dark': '#059669',
    warning: '#EAB308',
    danger: '#EF4444',
  };
  const inlineColor = color ? colorValueMap[color] : undefined;

  return (
    <div className="w-full" title={label}>
      {showLabel && (
        <div className="flex justify-between mb-1">
          <span className="text-xs font-semibold text-gray-500">{label}</span>
          <span className="text-xs font-bold text-gray-700">{percentage}%</span>
        </div>
      )}
      <div className={`w-full bg-gray-200 rounded-full ${sizeClasses[size]} overflow-hidden`}>
        <div
          className={`${getColorClass()} ${sizeClasses[size]} rounded-full transition-all duration-500 ${animated ? 'transition-all duration-500' : ''}`}
          style={{
            width: `${percentage}%`,
            ...(inlineColor ? { backgroundColor: inlineColor } : {}),
          }}
        />
      </div>
    </div>
  );
};

export default ProgressBar;
